import { useMemo } from 'react';
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval, isSameMonth, isSameDay, isToday } from 'date-fns';
import { Plus, Clock, CalendarIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { Event } from '@/hooks/useEvents';

interface MonthViewProps {
  date: Date;
  events: Event[];
  onDateSelect?: (date: Date) => void;
  onEventSelect?: (event: Event) => void;
  onCreateEvent?: (date?: Date) => void;
  selectedDate: Date | null;
}

const eventTypeColors = {
  meeting: 'bg-blue-500 text-white',
  consultation: 'bg-green-500 text-white',
  site_visit: 'bg-orange-500 text-white',
  follow_up: 'bg-purple-500 text-white',
  presentation: 'bg-red-500 text-white',
  other: 'bg-gray-500 text-white',
};

export function MonthView({ date, events, onDateSelect, onEventSelect, onCreateEvent, selectedDate }: MonthViewProps) {
  const monthStart = startOfMonth(date);
  const monthEnd = endOfMonth(date);
  const calendarStart = startOfWeek(monthStart);
  const calendarEnd = endOfWeek(monthEnd);

  const calendarDays = eachDayOfInterval({
    start: calendarStart,
    end: calendarEnd,
  });

  const eventsByDate = useMemo(() => {
    const eventMap: Record<string, Event[]> = {};
    events.forEach(event => {
      const eventDate = format(new Date(event.start_time), 'yyyy-MM-dd');
      if (!eventMap[eventDate]) {
        eventMap[eventDate] = [];
      }
      eventMap[eventDate].push(event);
    });
    return eventMap;
  }, [events]);

  const handleDateClick = (clickedDate: Date) => {
    onDateSelect?.(clickedDate);
  };

  const getEventsForDate = (checkDate: Date) => {
    const dateKey = format(checkDate, 'yyyy-MM-dd');
    return eventsByDate[dateKey] || [];
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
      {/* Calendar Grid */}
      <Card className="lg:col-span-3">
        <CardContent className="p-6">
          {/* Weekday Headers */}
          <div className="grid grid-cols-7 gap-1 mb-4">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
              <div key={day} className="p-2 text-center text-sm font-medium text-muted-foreground">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Days */}
          <div className="grid grid-cols-7 gap-1">
            {calendarDays.map((day) => {
              const dayEvents = getEventsForDate(day);
              const isCurrentMonth = isSameMonth(day, date);
              const isDayToday = isToday(day);
              const isSelected = selectedDate && isSameDay(day, selectedDate);

              return (
                <div
                  key={day.toISOString()}
                  className={cn(
                    "min-h-[100px] p-2 border border-border cursor-pointer transition-colors",
                    !isCurrentMonth && "bg-muted/50 text-muted-foreground",
                    isDayToday && "bg-primary/10 border-primary",
                    isSelected && "bg-accent border-accent-foreground",
                    "hover:bg-accent/50"
                  )}
                  onClick={() => handleDateClick(day)}
                >
                  <div className={cn(
                    "text-sm font-medium mb-1",
                    isDayToday && "text-primary font-bold"
                  )}>
                    {format(day, 'd')}
                  </div>
                  <div className="space-y-1">
                    {dayEvents.slice(0, 3).map((event) => (
                      <div
                        key={event.id}
                        className={cn(
                          "text-xs p-1 rounded cursor-pointer truncate",
                          eventTypeColors[event.event_type as keyof typeof eventTypeColors] || eventTypeColors.other
                        )}
                        onClick={(e) => {
                          e.stopPropagation();
                          onEventSelect?.(event);
                        }}
                      >
                        {format(new Date(event.start_time), 'HH:mm')} {event.title}
                      </div>
                    ))}
                    {dayEvents.length > 3 && (
                      <div className="text-xs text-muted-foreground">
                        +{dayEvents.length - 3} more
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Selected Date Events */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">
            {selectedDate ? format(selectedDate, 'MMM dd, yyyy') : 'Select a date'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {selectedDate ? (
            <div className="space-y-3">
              {getEventsForDate(selectedDate).length === 0 ? (
                <div className="text-center text-muted-foreground py-4">
                  <Clock className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>No events scheduled</p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-2"
                    onClick={() => onCreateEvent?.(selectedDate)}
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    Add Event
                  </Button>
                </div>
              ) : (
                getEventsForDate(selectedDate).map((event) => (
                  <div
                    key={event.id}
                    className="p-3 border border-border rounded-lg cursor-pointer hover:bg-accent transition-colors"
                    onClick={() => onEventSelect?.(event)}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="font-medium truncate">{event.title}</h4>
                      <Badge
                        variant="outline"
                        className={cn(
                          "text-xs",
                          eventTypeColors[event.event_type as keyof typeof eventTypeColors] || eventTypeColors.other
                        )}
                      >
                        {event.event_type}
                      </Badge>
                    </div>
                    <div className="space-y-1 text-sm text-muted-foreground">
                      <div className="flex items-center">
                        <Clock className="h-3 w-3 mr-1" />
                        {format(new Date(event.start_time), 'HH:mm')} - {format(new Date(event.end_time), 'HH:mm')}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          ) : (
            <div className="text-center text-muted-foreground py-8">
              <CalendarIcon className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>Click on a date to view events</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
